import { Injectable } from '@angular/core';
import {MetricsService} from './metrics.service';
import {FieldAnalyticsConfig, FieldConfig, FieldNameSource} from '../models/field-config';
import {GoogleAnalyticsEventsService} from './google-analytics-events.service';
import {FieldType} from '../models/field-type';
import {TranslateService} from '@ngx-translate/core';
import {Ga4EventPayload} from '../../core/models/ga4-event-payload';
import {GA4EventKey} from '../../core/consts/ga4-event-key.enum';
import {ValidationErrors} from '@angular/forms';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';
import {TextFieldEditConfig} from '../components/presentation/text-field-edit/text-field-edit.component';
import {ApplicationContextService} from '../../core/services/impl/configuration/application-context.service';

@Injectable({
  providedIn: 'root'
})
export class FormFieldService {

  get isDataFromAztec(): boolean {
    return this.metricsService.appStateService.state.dataFromAztec;
  }

  get autocompleteAllowed(): boolean {
    return !this.contextService.isDiagnosticStationContext;
  }

  constructor(public metricsService: MetricsService,
              private contextService: ApplicationContextService,
              private googleAnalyticsService: GoogleAnalyticsEventsService,
              private ts: TranslateService) { }

  onClick(fieldConfig: FieldConfig, fieldValue: any): void {
    if (this.isEventAllowed(fieldConfig, GA4EventKey.FIELD_CLICK)) {
      const payload: Ga4EventPayload = {
        calculator_field_name: this.fieldCaptionTranslated(fieldConfig)
      };
      if (this.shouldIncludeValueInClickEvent(fieldConfig)) {
        if (fieldConfig.analyticsConfig && fieldConfig.analyticsConfig.customFieldValue) {
          payload.calculator_field_value = fieldConfig.analyticsConfig.customFieldValue();
        } else {
          payload.calculator_field_value = fieldValue;
        }
      }
      this.googleAnalyticsService.send(GA4EventKey.FIELD_CLICK, payload);
    }
  }

  onTextFieldEditActionClick(config: TextFieldEditConfig): void {
    const payload: Ga4EventPayload = {
      calculator_field_name: `${this.ts.instant(config.fieldLabel)} ${this.ts.instant(config.actionLabel)}`
    };
    if (this.shouldIncludeValueInTextFieldEditClickEvent(config)) {
      payload.calculator_field_value = config.value;
    }
    this.googleAnalyticsService.send(GA4EventKey.FIELD_CLICK, payload);
  }

  onError(fieldConfig: FieldConfig, errors: string[]): void {
    if (this.isEventAllowed(fieldConfig, GA4EventKey.FIELD_ERROR)) {
      const errorsTranslated = this.getTranslatedErrors(errors);
      errorsTranslated.forEach(errorMessage => this.sendErrorEvent(fieldConfig, errorMessage));
    }
  }

  getTranslatedErrors(errors: string[]): string[] {
    return errors.map(error => {
      let translation = this.ts.instant(error);
      if (translation === error && translation.includes('.')) {
        const keys = translation.split('.');
        if (!!keys.length) {
          translation = this.ts.instant(keys[keys.length - 1]);
        }
      }
      return translation;
    });
  }

  fieldCaptionTranslated(fieldConfig: FieldConfig): string {
    if (fieldConfig.analyticsConfig && fieldConfig.analyticsConfig.customFieldName) {
      return this.ts.instant(fieldConfig.analyticsConfig.customFieldName);
    }
    const fieldNameSource = this.getAnalyticsFieldNameSource(fieldConfig);
    switch (fieldNameSource) {
      case FieldNameSource.CAPTION:
        return this.ts.instant(fieldConfig.caption);
      case FieldNameSource.SELECT_CONTENT_TEXT:
        return this.ts.instant(fieldConfig.select.contentText);
      default:
        if (fieldConfig.type === FieldType.Checkbox) {
          return fieldConfig.fieldKey;
        }
        if (fieldConfig.caption) {
          return this.ts.instant(fieldConfig.caption);
        }
    }
    return null;
  }

  fieldClickEventSent(fieldConfig: FieldConfig): boolean {
    return this.googleAnalyticsService.eventSent(GA4EventKey.FIELD_CLICK, {
      calculator_field_name: this.fieldCaptionTranslated(fieldConfig)
    });
  }

  private sendErrorEvent(fieldConfig: FieldConfig, errorMessage: string): void {
    if (errorMessage && !!errorMessage.length) {
      const payload: Ga4EventPayload = {
        calculator_field_name: this.fieldCaptionTranslated(fieldConfig),
        calculator_error_message: errorMessage
      };
      this.googleAnalyticsService.send(GA4EventKey.FIELD_ERROR, payload);
    }
  }

  private isEventAllowed(fieldConfig: FieldConfig, eventKey: string): boolean {
    if (!!fieldConfig.analyticsConfig && fieldConfig.analyticsConfig.eventsToSkip) {
      return !fieldConfig.analyticsConfig.eventsToSkip.includes(eventKey);
    }
    return true;
  }

  private shouldIncludeValueInTextFieldEditClickEvent(config: TextFieldEditConfig): boolean {
    return !(config && config.analyticsConfig && config.analyticsConfig.skipFieldValue);
  }

  private shouldIncludeValueInClickEvent(fieldConfig: FieldConfig): boolean {
    if ([FieldType.TextField, FieldType.TextArea].includes(<string>fieldConfig.type)) {
      if (fieldConfig && fieldConfig.analyticsConfig && isNotNullOrUndefined(fieldConfig.analyticsConfig.includeFieldValue)) {
        return fieldConfig.analyticsConfig.includeFieldValue;
      }
      return false;
    } else {
      if (fieldConfig && fieldConfig.analyticsConfig && fieldConfig.analyticsConfig.skipFieldValue) {
        return false;
      }
      return true;
    }
  }

  private getAnalyticsFieldNameSource(fieldConfig: FieldConfig): FieldNameSource | null {
    if (fieldConfig.analyticsConfig && fieldConfig.analyticsConfig.fieldNameSource) {
      return fieldConfig.analyticsConfig.fieldNameSource;
    }
    return null;
  }
}
